import { useState, FormEvent } from 'react';

export default function Footer() {
  const [email, setEmail] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState<'idle' | 'success' | 'error'>('idle');

  const scrollToSection = (sectionId: string) => {
    const element = document.getElementById(sectionId);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
  };

  const handleNewsletterSubmit = async (e: FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    setSubmitStatus('idle');

    try {
      const formBody = new URLSearchParams();
      formBody.append('email', email);

      const response = await fetch('https://readdy.ai/api/form/d6os4v930mftb86r3qk0', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
        },
        body: formBody.toString()
      });

      if (response.ok) {
        setSubmitStatus('success');
        setEmail('');
      } else {
        setSubmitStatus('error');
      }
    } catch (error) {
      setSubmitStatus('error');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <footer className="bg-slate-900 text-white pt-16 pb-8 px-6">
      <div className="max-w-7xl mx-auto">
        <div className="flex flex-col md:flex-row justify-between items-start md:items-center mb-12 pb-8 border-b border-gray-700">
          <div className="mb-4 md:mb-0">
            <img
              src="https://static.readdy.ai/image/ac89004c0dc4ecdcf411495653d0d9a7/f9faacd40ba94040d63ca5a9983e5de2.jpeg"
              alt="Monarch Xpress LLC"
              className="h-12 w-auto mb-2"
            />
            <div className="text-sm text-gray-400">Monarch Xpress LLC</div>
          </div>
          <div className="text-sm text-gray-400">
            Your Trusted Partner in Freight Transportation
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-4 gap-8 mb-12">
          <div>
            <h4 className="font-bold text-white mb-4">Services</h4>
            <ul className="space-y-2 text-sm text-gray-400">
              <li>
                <button onClick={() => scrollToSection('services')} className="hover:text-green-400 transition-colors cursor-pointer">
                  Dry Van Transport
                </button>
              </li>
              <li>
                <button onClick={() => scrollToSection('services')} className="hover:text-green-400 transition-colors cursor-pointer">
                  Flatbed Hauling
                </button>
              </li>
              <li>
                <button onClick={() => scrollToSection('services')} className="hover:text-green-400 transition-colors cursor-pointer">
                  Cross Dock Services
                </button>
              </li>
              <li>
                <button onClick={() => scrollToSection('services')} className="hover:text-green-400 transition-colors cursor-pointer">
                  Warehousing
                </button>
              </li>
            </ul>
          </div>

          <div>
            <h4 className="font-bold text-white mb-4">Company</h4>
            <ul className="space-y-2 text-sm text-gray-400">
              <li>
                <button onClick={() => scrollToSection('coverage')} className="hover:text-green-400 transition-colors cursor-pointer">
                  About Us
                </button>
              </li>
              <li>
                <button onClick={() => scrollToSection('fleet')} className="hover:text-green-400 transition-colors cursor-pointer">
                  Our Fleet
                </button>
              </li>
              <li>
                <button onClick={() => scrollToSection('coverage')} className="hover:text-green-400 transition-colors cursor-pointer">
                  Coverage Areas
                </button>
              </li>
              <li>
                <button onClick={() => scrollToSection('recruiting')} className="hover:text-green-400 transition-colors cursor-pointer">
                  Careers
                </button>
              </li>
            </ul>
          </div>

          <div>
            <h4 className="font-bold text-white mb-4">Resources</h4>
            <ul className="space-y-2 text-sm text-gray-400">
              <li>
                <a href="#" className="hover:text-green-400 transition-colors">
                  Safety &amp; Compliance
                </a>
              </li>
              <li>
                <a href="#" className="hover:text-green-400 transition-colors">
                  Track Shipment
                </a>
              </li>
              <li>
                <a href="#" className="hover:text-green-400 transition-colors">
                  Customer Portal
                </a>
              </li>
              <li>
                <button onClick={() => scrollToSection('contact')} className="hover:text-green-400 transition-colors cursor-pointer">
                  Get a Quote
                </button>
              </li>
            </ul>
          </div>

          <div>
            <h4 className="font-bold text-white mb-4">Newsletter</h4>
            <p className="text-sm text-gray-400 mb-4">
              Stay updated on industry news and company updates
            </p>
            <form id="newsletter-form" data-readdy-form onSubmit={handleNewsletterSubmit} className="flex gap-2">
              <input
                type="email"
                name="email"
                placeholder="Your email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                className="flex-1 px-4 py-2 bg-slate-800 border border-gray-700 rounded-lg focus:border-green-600 focus:outline-none text-sm text-white"
              />
              <button
                type="submit"
                disabled={isSubmitting}
                className="bg-green-600 text-white px-4 py-2 rounded-lg hover:bg-green-700 transition-colors flex items-center justify-center disabled:opacity-50 whitespace-nowrap cursor-pointer"
              >
                <i className="ri-arrow-right-line"></i>
              </button>
            </form>
            {submitStatus === 'success' && (
              <div className="text-green-400 text-xs mt-2">Subscribed successfully!</div>
            )}
            {submitStatus === 'error' && (
              <div className="text-red-400 text-xs mt-2">Something went wrong. Try again.</div>
            )}
          </div>
        </div>

        <div className="flex flex-col md:flex-row justify-between items-center pt-8 border-t border-gray-700">
          <div className="text-sm text-gray-400 mb-4 md:mb-0">
            © 2025 Monarch Xpress LLC. All rights reserved.
          </div>
          <div className="flex gap-4">
            <a href="#" className="w-10 h-10 bg-slate-800 rounded-full flex items-center justify-center hover:bg-green-600 transition-colors cursor-pointer">
              <i className="ri-linkedin-fill"></i>
            </a>
            <a href="#" className="w-10 h-10 bg-slate-800 rounded-full flex items-center justify-center hover:bg-green-600 transition-colors cursor-pointer">
              <i className="ri-facebook-fill"></i>
            </a>
            <a href="#" className="w-10 h-10 bg-slate-800 rounded-full flex items-center justify-center hover:bg-green-600 transition-colors cursor-pointer">
              <i className="ri-instagram-fill"></i>
            </a>
            <a href="#" className="w-10 h-10 bg-slate-800 rounded-full flex items-center justify-center hover:bg-green-600 transition-colors cursor-pointer">
              <i className="ri-youtube-fill"></i>
            </a>
          </div>
        </div>
      </div>
    </footer>
  );
}